// notify.ts is the single wrapper over Mantine notifications; every other toast goes through it.
// eslint-disable-next-line no-restricted-imports
import { notifications, type NotificationData } from '@mantine/notifications';
import { getUserFacingErrorMessage } from './userFacingError';

export type NotifyType = 'success' | 'error' | 'warning' | 'info';

const COLOR_BY_TYPE = {
  success: 'green',
  error: 'red',
  warning: 'yellow',
  info: 'blue',
} as const satisfies Record<NotifyType, string>;

export interface NotifyOptions extends Omit<NotificationData, 'color' | 'title' | 'message'> {
  type: NotifyType;
  title?: NotificationData['title'];
  /** Authored text. For `type: 'error'` with `error`, this is the production fallback. */
  message?: NotificationData['message'];
  /** Caught error. Its text is shown outside production and only if allowlisted in production. */
  error?: unknown;
}

/** Show a toast. The `type` decides the color; returns the notification id. */
export function notify({ type, error, message, ...rest }: NotifyOptions): string {
  const body =
    type === 'error' && error !== undefined
      ? getUserFacingErrorMessage(error, typeof message === 'string' ? message : '')
      : message;
  return notifications.show({ ...rest, color: COLOR_BY_TYPE[type], message: body ?? '' });
}

/** Close a toast opened by `notify` (e.g. a persistent one with an action). */
export function dismissNotify(id: string): void {
  notifications.hide(id);
}
